import { BloodGlucoseSession, CoughSession, DeviceTypeInteraction, DeviceTypes, Session } from "../misc/api/apiTypes";
import { Device } from "../misc/api/DeviceLinkApi/Devices";
import { DeviceNotification } from "../misc/api/DeviceLinkApi/Models/DeviceNotification";
import { Visit } from "webportalclient";

export interface DeviceUploadStatus {
    id: string;
    status: UploadStatus;
}

export enum UploadStatus {
    Uploaded = 0,
    Uploading = 1,
    Failed = 2,
    PartialFailed = 3
}

export enum UpdateStatus {
    UpToDate = 0,
    Updating = 1,
    FailedToUpdate = 9,
}

export interface DevicesState {
    devices: Array<Device>;
    deviceInteractions: Array<DeviceTypeInteraction>;
    isInteractionModalVisible: boolean;
    isOmniInteractionModalVisible: boolean;
    isPairingModalVisible: boolean;
    interactionModalStep: number;
    activeSelectedDevice: Device;
    selectedDevices: Array<Device>;
    deviceResultsCount: number;
    isFetching: boolean;
    isApiFetching: boolean;
    updateStatus: UpdateStatus;
    selectedAction: string;
    currentStatusText: string;
    upload: {
        parentVisitCandidates: Visit[];
        parentSessionCandidates: Session[];
        isFetchingSessionCandidates: boolean;
        selectedSessionIds: Array<string>;
        activeSelectedSessionId: string;
        notifications: Array<DeviceNotification>;
        deviceStatus: Array<DeviceUploadStatus>;
    };
    mail: {
        mailAddress: string;
        trackingNumber: string;
        isFetchingAddress: boolean;
    };
}

export const initialDevicesState = {
    devices: [],
    isInteractionModalVisible: false,
    isOmniInteractionModalVisible: false,
    isPairingModalVisible: false,
    interactionModalStep: null,
    activeSelectedDevice: null,
    selectedDevices: [],
    deviceResultsCount: null,
    isFetching: false,
    isApiFetching: false,
    updateStatus: UpdateStatus.UpToDate,
    selectedAction: null,
    currentStatusText: null,
    upload: {
        parentSessionCandidates: [],
        isFetchingSessionCandidates: false,
        selectedSessionIds: [],
        activeSelectedSessionId: null,
        notifications: [],
        deviceStatus: [],
    },
    mail: {
        mailAddress: null,
        trackingNumber: null,
        isFetchingAddress: false,
    }
} as DevicesState;

export enum DevicesActionType {
    LaunchInteractionModal = 'LaunchInteractionModal',
    LaunchOmniInteractionModal = "LaunchOmniInteractionModal",
    CloseInteractionModal = 'CloseInteractionModal',
    RequestDevices = "RequestDevices",
    ReceiveDevices = "ReceiveDevices",
    ReceiveDeviceInteractions = 'ReceiveDeviceInteractions',
    ReceiveDeviceNotifications = "ReceiveDeviceNotifications",
    SetInteractionModalStep = "SetInteractionModalStep",
    IsFetching = "IsFetching",
    SelectAction = "SelectAction",
    SelectSessionsForUpload = "SelectSessionsForUpload",
    MailConfirmed = "MailConfirmed",
    SetUploadStatus = "SetUploadStatus",
    SetDeviceOverallUploadStatus = "SetDeviceOverallUploadStatus",
    SelectActiveSessionForUpload = "SelectActiveSessionForUpload",
    ReceiveMailAddress = "ReceiveMailAddress",
    ReceiveDeviceSessions = "ReceiveDeviceSessions"
}

export class LaunchInteractionModalAction {
    type: DevicesActionType.LaunchInteractionModal;
    selectedDevice: Device;

    static create(selectedDevice: Device): LaunchInteractionModalAction {
        return {
            type: DevicesActionType.LaunchInteractionModal,
            selectedDevice: selectedDevice,
        }
    }
}

export class LaunchOmniInteractionModalAction {
    type: DevicesActionType.LaunchOmniInteractionModal;
    selectedDeviceTypes: DeviceTypes;

    static create(selectedDeviceTypes: DeviceTypes): LaunchOmniInteractionModalAction {
        return {
            type: DevicesActionType.LaunchOmniInteractionModal,
            selectedDeviceTypes: selectedDeviceTypes,
        }
    }
}

export class CloseInteractionModalAction {
    type: DevicesActionType.CloseInteractionModal;

    static create(): CloseInteractionModalAction {
        return {
            type: DevicesActionType.CloseInteractionModal,
        }
    }
}

export class RequestDevicesAction {
    type: DevicesActionType.RequestDevices;

    static create(): RequestDevicesAction {
        return {
            type: DevicesActionType.RequestDevices,
        }
    }
}

export class ReceiveDevicesAction {
    type: DevicesActionType.ReceiveDevices;
    devices: Array<Device>;

    static create(devices: Array<Device>): ReceiveDevicesAction {
        return {
            type: DevicesActionType.ReceiveDevices,
            devices: devices,
        }
    }
}

export class SetInteractionModalStepAction {
    type: DevicesActionType.SetInteractionModalStep;
    step: number;

    static create(step: number): SetInteractionModalStepAction {
        return {
            type: DevicesActionType.SetInteractionModalStep,
            step: step
        };
    }
}

export class ReceiveDeviceInteractionsAction {
    type: DevicesActionType.ReceiveDeviceInteractions;
    interactions: Array<DeviceTypeInteraction>;

    static create(interactions: Array<DeviceTypeInteraction>): ReceiveDeviceInteractionsAction {
        return {
            type: DevicesActionType.ReceiveDeviceInteractions,
            interactions: interactions,
        }
    }
}

export class ReceiveDeviceNotificationsAction {
    type: DevicesActionType.ReceiveDeviceNotifications;
    notifications: Array<DeviceNotification>;

    static create(notifications: Array<DeviceNotification>): ReceiveDeviceNotificationsAction {
        return {
            type: DevicesActionType.ReceiveDeviceNotifications,
            notifications: notifications,
        }
    }
}

export class IsFetchingAction {
    type: DevicesActionType.IsFetching;

    static create(): IsFetchingAction {
        return {
            type: DevicesActionType.IsFetching,
        }
    }
}

export class SelectActionAction {
    type: DevicesActionType.SelectAction;
    action: string;

    static create(action: string): SelectActionAction {
        return {
            type: DevicesActionType.SelectAction,
            action: action,
        }
    }
}

export class SelectSessionsForUploadAction {
    type: DevicesActionType.SelectSessionsForUpload;
    sessionIds: Array<string>;

    static create(id: Array<string>): SelectSessionsForUploadAction {
        return {
            type: DevicesActionType.SelectSessionsForUpload,
            sessionIds: id,
        }
    }
}

export class MailConfirmedAction {
    type: DevicesActionType.MailConfirmed;
    trackingNumber: string;

    static create(trackingNumber: string): MailConfirmedAction {
        return {
            type: DevicesActionType.MailConfirmed,
            trackingNumber: trackingNumber,
        }
    }
}

export class SetUploadStatusAction {
    type: DevicesActionType.SetUploadStatus;
    status: string;

    static create(status: string): SetUploadStatusAction {
        return {
            type: DevicesActionType.SetUploadStatus,
            status: status,
        }
    }
}

export class SetDeviceOverallUploadStatusAction {
    type: DevicesActionType.SetDeviceOverallUploadStatus;
    id: string;
    status: UploadStatus;
    message?: string;

    static create(id: string, status: UploadStatus, message: string = undefined): SetDeviceOverallUploadStatusAction {
        return {
            type: DevicesActionType.SetDeviceOverallUploadStatus,
            id: id,
            status: status,
            message: message,
        }
    }
}

export class SelectActiveSessionForUploadAction {
    type: DevicesActionType.SelectActiveSessionForUpload;
    sessionId: string;

    static create(id: string): SelectActiveSessionForUploadAction {
        return {
            type: DevicesActionType.SelectActiveSessionForUpload,
            sessionId: id,
        }
    }
}

export class ReceiveMailAddressAction {
    type: DevicesActionType.ReceiveMailAddress;
    mailAddress: any;

    static create(mailAddress: any): ReceiveMailAddressAction {
        return {
            type: DevicesActionType.ReceiveMailAddress,
            mailAddress: mailAddress,
        }
    }
}

export class ReceiveDeviceSessionsAction {
    type: DevicesActionType.ReceiveDeviceSessions;
    visitCandidates: Array<Visit>;
    sessionCandidates: Array<BloodGlucoseSession | CoughSession>;
    resultCount: number;

    static create(resultCount: number, sessionCandidates: Array<BloodGlucoseSession | CoughSession>, visitCandidates: Visit[]): ReceiveDeviceSessionsAction {
        return {
            type: DevicesActionType.ReceiveDeviceSessions,
            visitCandidates: visitCandidates,
            sessionCandidates: sessionCandidates,
            resultCount: resultCount,
        }
    }
}

export type DevicesAction = LaunchInteractionModalAction | LaunchOmniInteractionModalAction | CloseInteractionModalAction | RequestDevicesAction | ReceiveDevicesAction | SetInteractionModalStepAction | ReceiveDeviceInteractionsAction | ReceiveDeviceNotificationsAction | IsFetchingAction | SelectActionAction | SelectSessionsForUploadAction | MailConfirmedAction | SetUploadStatusAction | SetDeviceOverallUploadStatusAction | SelectActiveSessionForUploadAction | ReceiveMailAddressAction | ReceiveDeviceSessionsAction;

export const devicesReducer = (state: DevicesState, action: DevicesAction): DevicesState => {
    switch (action.type) {
        case DevicesActionType.LaunchInteractionModal:
            return {
                ...state,
                isInteractionModalVisible: true,
                interactionModalStep: 1,
                selectedDevices: [action.selectedDevice],
                activeSelectedDevice: action.selectedDevice,
            };
        case DevicesActionType.LaunchOmniInteractionModal:
            return {
                ...state,
                isOmniInteractionModalVisible: true,
                interactionModalStep: 1,
                selectedDevices: state.devices.filter(x => x.type === action.selectedDeviceTypes),
            };
        case DevicesActionType.CloseInteractionModal:
            return {
                ...state,
                isInteractionModalVisible: false,
                isOmniInteractionModalVisible: false,
                selectedDevices: [],
                activeSelectedDevice: null,
                currentStatusText: null,
                upload: initialDevicesState.upload,
                isApiFetching: false,
            };
        case DevicesActionType.RequestDevices:
            return {
                ...state,
                isFetching: true,
                isPairingModalVisible: false,
            };
        case DevicesActionType.ReceiveDevices:
            return {
                ...state,
                devices: action.devices,
                isFetching: false,
                upload: {
                    ...state.upload,
                    deviceStatus: [],
                },
            };
        case DevicesActionType.SetInteractionModalStep:
            return {
                ...state,
                interactionModalStep: action.step,
            };
        case DevicesActionType.ReceiveDeviceInteractions:
            return {
                ...state,
                deviceInteractions: action.interactions,
            };
        case DevicesActionType.ReceiveDeviceNotifications:
            return {
                ...state,
                upload: {
                    ...state.upload,
                    notifications: action.notifications,
                },
            };
        case DevicesActionType.IsFetching:
            return {
                ...state,
                isApiFetching: true,
            };
        case DevicesActionType.SelectAction:
            return {
                ...state,
                selectedAction: action.action,
            };
        case DevicesActionType.SelectSessionsForUpload:
            return {
                ...state,
                upload: {
                    ...state.upload,
                    selectedSessionIds: action.sessionIds,
                },
            };
        case DevicesActionType.MailConfirmed:
            return {
                ...state,
                interactionModalStep: 4,
                currentStatusText: 'Data marked to be mailed',
            };
        case DevicesActionType.SetUploadStatus:
            return {
                ...state,
                currentStatusText: action.status,
                upload: {
                    ...state.upload,
                },
            };
        case DevicesActionType.SetDeviceOverallUploadStatus:
            const deviceStatus = state.upload.deviceStatus;
            const device = deviceStatus.find(x => x.id === action.id);

            if (!device) {
                deviceStatus.push({ id: action.id, status: action.status });
            }

            return {
                ...state,
                currentStatusText: action.message ? action.message : state.currentStatusText,
                upload: {
                    ...state.upload,
                    deviceStatus: deviceStatus.map((device) => device.id === action.id ? { ...device, status: action.status } : device),
                },
            };
        case DevicesActionType.SelectActiveSessionForUpload:
            return {
                ...state,
                upload: {
                    ...state.upload,
                    activeSelectedSessionId: action.sessionId,
                },
            };
            case DevicesActionType.ReceiveMailAddress:
            return {
                ...state,
                mail: {
                    ...state.mail,
                    mailAddress: action.mailAddress,
                    isFetchingAddress: false,
                },
            };
        case DevicesActionType.ReceiveDeviceSessions:
            return {
                ...state,
                deviceResultsCount: action.resultCount,
                upload: {
                    ...state.upload,
                    isFetchingSessionCandidates: false,
                    parentSessionCandidates: action.sessionCandidates,
                    parentVisitCandidates: action.visitCandidates,
                },
            };
        default:
            return state;
    }
}
